// Page1.js - Address Search & Map Selection
'use strict';

import { logger, FIELDS } from '../utils/logger.js';

export default class Page1 {
    constructor(widget) {
      this.widget = widget;
      this.shadowRoot = widget.shadowRoot;
      this.config = widget.config;

      // Map-related properties
      this.map = null;
      this.centerPin = null;
      this.hasChangedFromDefault = false;

      // Places API retry tracking
      this.consecutiveSearchFailures = 0;
      this.maxConsecutiveSearchFailures = 3;
    }

    /**
     * Generic retry function with exponential backoff
     * @param {Function} operation - Async function to retry
     * @param {Object} options - Retry configuration
     * @param {number} options.maxRetries - Maximum number of retry attempts (default: 3)
     * @param {number} options.initialDelay - Initial delay in ms (default: 1000)
     * @param {number} options.maxDelay - Maximum delay in ms (default: 5000)
     * @param {string} options.operationName - Name for logging
     * @param {string} options.errorType - Error type for session storage when retries exhausted
     * @param {Function} options.shouldRetry - Optional function to determine if error should trigger retry
     * @returns {Promise} - Result from operation or triggers fallthrough
     */
    async retryWithBackoff(operation, options = {}) {
      const {
        maxRetries = 3,
        initialDelay = 1000,
        maxDelay = 5000,
        operationName = 'operation',
        errorType = 'retry_exhausted',
        shouldRetry = null
      } = options;

      let lastError;

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const result = await operation();
          return result;

        } catch (error) {
          lastError = error;

          // Check if we should retry this error
          if (shouldRetry && !shouldRetry(error)) {
            break;
          }

          // If this was the last attempt, break out
          if (attempt === maxRetries) {
            break;
          }

          // Calculate exponential backoff delay: initialDelay * 2^attempt, capped at maxDelay
          const delay = Math.min(initialDelay * Math.pow(2, attempt), maxDelay);

          // Wait before next retry
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }

      // All retries exhausted - log error and trigger fallthrough
      logger.error({
        [FIELDS.ERROR]: lastError,
        [FIELDS.OPERATION]: operationName,
        [FIELDS.PAGE_NUMBER]: 1,
        [FIELDS.REASON]: 'retry_exhausted',
        maxRetries,
        errorType
      }, `${operationName} failed after ${maxRetries} retries`);

      // Store error context and navigate to fallthrough page
      this.widget.setSessionData('error_context', {
        errorType,
        operation: operationName,
        attempts: maxRetries + 1,
        lastError: lastError?.message || 'Unknown error'
      });
      this.widget.showPage('fallthrough');

      // Throw to prevent further execution
      throw lastError;
    }

    render() {
      return `
        <!-- Page 1: Address Search & Map Selection -->
        <div class="page" id="page-1" style="display: block;">
          <!-- Header Section -->
          <div class="page-header">
            <h2 class="page-title">Select Your Location</h2>
            <p class="page-subtitle">Follow the steps below to set your exact location for solar analysis</p>

            <!-- Step Indicator -->
            <div class="step-indicator">
              <div class="step active" id="step-1">
                <span class="step-number">1</span>
                <span class="step-text">Enter Address</span>
              </div>
              <div class="step-divider" id="divider-1"></div>
              <div class="step" id="step-2">
                <span class="step-number">2</span>
                <span class="step-text">Pin Location</span>
              </div>
              <div class="step-divider" id="divider-2"></div>
              <div class="step" id="step-3">
                <span class="step-number">3</span>
                <span class="step-text">Scan Roof</span>
              </div>
            </div>
          </div>

          <div class="error-message" id="error-message" style="display: none;"></div>

          <!-- Search Section -->
          <div class="search-section">
            <div class="search-container">
              <svg class="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
              <input
                type="text"
                id="address-input"
                class="search-input"
                placeholder="Enter an address, city, or ZIP code"
                autocomplete="off"
              >
              <div class="loading-indicator" id="loading-indicator" style="display: none;">
                <div class="spinner"></div>
              </div>
              <div class="suggestions-dropdown" id="suggestions-dropdown"></div>
            </div>
          </div>

          <!-- Map Section -->
          <div class="map-container" id="map-container-wrapper">
            <div class="map-placeholder" id="map-placeholder">
              <svg class="placeholder-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"></path>
              </svg>
              <div class="placeholder-text">Map Ready</div>
              <div class="placeholder-subtext">Drag the map to position the center pin</div>
            </div>
            <div class="helper-text" id="helper-text" style="display: none;">Drag the map to position the center pin on your roof</div>

            <!-- Center Pin -->
            <div class="center-pin-overlay" id="center-pin-overlay">
              <svg class="map-pin-svg" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#ef4444"/>
                <circle cx="12" cy="9" r="2.5" fill="white"/>
              </svg>
            </div>

            <!-- Pulse Effect -->
            <div class="location-pulse" id="location-pulse"></div>

            <!-- Center Dot -->
            <div class="location-dot" id="location-dot"></div>

            <div id="map"></div>
          </div>

          <!-- Footer Section -->
          <div class="page-footer">
            <button class="confirm-btn" id="scan-roof-button" disabled>
              <span>Scan Roof Now</span>
            </button>

            <!-- Debug Section (preserved for testing) -->
            <div class="debug-section">
              <strong>Plugin Configuration Debug:</strong><br>
              API Key: ${this.config.apiKey || 'undefined'}<br>
              Server URL: ${this.config.serverUrl || 'undefined'}<br>
              Google Maps Key: ${this.config.googleMapsApiKey ? this.config.googleMapsApiKey.substring(0, 20) + '...' : 'undefined'}<br>
              Default Location: ${this.config.defaultLocation ? `${this.config.defaultLocation.address} (${this.config.defaultLocation.lat}, ${this.config.defaultLocation.lng})` : 'undefined'}<br>
              Theme: ${this.config.theme ? JSON.stringify(this.config.theme) : 'undefined'}
            </div>
          </div>
        </div>
      `;
    }

    onMount() {
      // Initialize Google Maps
      this.initializeGoogleMaps();

      // Setup address search
      this.setupAddressSearch();

      // Setup scan roof button event listener
      this.setupScanRoofButton();
    }

    onUnmount() {
      // Clean up center pin elements
      const centerPinOverlay = this.shadowRoot.querySelector('#center-pin-overlay');
      const locationPulse = this.shadowRoot.querySelector('#location-pulse');
      const locationDot = this.shadowRoot.querySelector('#location-dot');

      if (centerPinOverlay) {
        centerPinOverlay.classList.remove('visible');
      }
      if (locationPulse) {
        locationPulse.classList.remove('visible');
      }
      if (locationDot) {
        locationDot.classList.remove('visible');
      }

      this.centerPin = null;
    }

    async initializeGoogleMaps() {
      // Check if we have the required configuration (fail fast - no retry)
      if (!this.config.googleMapsApiKey) {
        logger.error({
          [FIELDS.OPERATION]: 'initializeGoogleMaps',
          [FIELDS.PAGE_NUMBER]: 1,
          [FIELDS.REASON]: 'missing_api_key'
        }, 'Google Maps API key not found in configuration');

        // Navigate to fallthrough immediately for configuration errors
        this.widget.setSessionData('error_context', {
          errorType: 'map_config_error',
          operation: 'initializeGoogleMaps',
          lastError: 'Missing Google Maps API key'
        });
        this.widget.showPage('fallthrough');
        return;
      }

      // Retry loading Google Maps API with exponential backoff
      await this.retryWithBackoff(
        async () => {
          // Load Google Maps API if not loaded
          if (!window.google || !window.google.maps) {
            await this.loadGoogleMapsAPI();
          }

          // Get the map container
          const mapContainer = this.shadowRoot.querySelector('#map');
          if (!mapContainer) {
            throw new Error('Map container not found in DOM');
          }

          // Use default location from config
          const defaultLocation = this.config.defaultLocation || {
            lat: 51.0447,
            lng: -114.0719,
            zoom: 13
          };

          // Initialize the map - show default location immediately but make it non-interactive
          this.map = new google.maps.Map(mapContainer, {
            center: { lat: defaultLocation.lat, lng: defaultLocation.lng },
            zoom: defaultLocation.zoom,
            mapTypeId: 'satellite',
            mapId: 'DEMO_MAP_ID',
            tilt: 0,
            gestureHandling: 'none',
            zoomControl: false,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: false,
            keyboardShortcuts: false,
            clickableIcons: false,
            disableDefaultUI: true,
            panControl: false,
            scrollwheel: false,
            disableDoubleClickZoom: true,
            draggable: false
          });

          // Track if location has changed from default
          this.hasChangedFromDefault = false;

          // Initialize center pin (hidden until address is selected)
          this.centerPin = null;

          // Hide map placeholder immediately since we're showing the actual map
          const mapPlaceholder = this.shadowRoot.querySelector('#map-placeholder');
          if (mapPlaceholder) {
            mapPlaceholder.classList.add('hidden');
          }
        },
        {
          maxRetries: 3,
          initialDelay: 1000,
          maxDelay: 5000,
          operationName: 'initializeGoogleMaps',
          errorType: 'map_load_failed'
        }
      );
    }

    setupAddressSearch() {
      const addressInput = this.shadowRoot.querySelector('#address-input');
      const loadingIndicator = this.shadowRoot.querySelector('#loading-indicator');
      const suggestionsContainer = this.shadowRoot.querySelector('#suggestions-dropdown');
      
      let searchTimeout;
      let selectedLocation = null;
      
      // Handle input changes
      addressInput.addEventListener('input', async (e) => {
        const query = e.target.value.trim();
        
        // Clear previous timeout
        if (searchTimeout) {
          clearTimeout(searchTimeout);
        }
        
        // Hide suggestions if query is too short
        if (query.length < 3) {
          this.hideSuggestions();
          this.clearSelection();
          return;
        }
        
        // Debounce the search
        searchTimeout = setTimeout(async () => {
          this.showLoading(true);
          const suggestions = await this.searchPlaces(query);
          this.showLoading(false);
          this.renderSuggestions(suggestions);
        }, 300);
      });
      
      // Store reference for later use
      this.selectedLocation = null;
    }

    setupScanRoofButton() {
      const scanButton = this.shadowRoot.querySelector('#scan-roof-button');
      if (scanButton && !scanButton.hasAttribute('data-handler-added')) {
        scanButton.setAttribute('data-handler-added', 'true');
        scanButton.addEventListener('click', () => this.startRoofScan());
      }
    }

    async searchPlaces(query) {
      try {
        if (!window.google || !window.google.maps || !window.google.maps.places) {
          this.consecutiveSearchFailures++;

          logger.error({
            [FIELDS.OPERATION]: 'searchPlaces',
            [FIELDS.PAGE_NUMBER]: 1,
            [FIELDS.REASON]: 'google_places_api_not_loaded',
            consecutiveFailures: this.consecutiveSearchFailures
          }, 'Google Places API not loaded');

          // Check if we've hit max consecutive failures
          if (this.consecutiveSearchFailures >= this.maxConsecutiveSearchFailures) {
            logger.error({
              [FIELDS.OPERATION]: 'searchPlaces',
              [FIELDS.PAGE_NUMBER]: 1,
              [FIELDS.REASON]: 'max_search_failures_exceeded',
              consecutiveFailures: this.consecutiveSearchFailures
            }, `Places API failed ${this.consecutiveSearchFailures} consecutive times - triggering fallthrough`);

            // Navigate to fallthrough
            this.widget.setSessionData('error_context', {
              errorType: 'places_api_failed',
              operation: 'searchPlaces',
              attempts: this.consecutiveSearchFailures,
              lastError: 'Google Places API not loaded after multiple attempts'
            });
            this.widget.showPage('fallthrough');
            return [];
          }

          // Show error message in dropdown after first failure
          return [{
            place_id: '__error__',
            formatted_address: 'Uh oh - not finding any places...',
            isError: true
          }];
        }

        const results = await this.searchPlacesWithGoogleAPI(query);

        // If no results found, show friendly message (but don't count as failure)
        if (!results || results.length === 0) {
          return [{
            place_id: '__no_results__',
            formatted_address: 'No places found - try a different search',
            isError: true
          }];
        }

        // Success - reset failure counter
        this.consecutiveSearchFailures = 0;
        return results;

      } catch (error) {
        this.consecutiveSearchFailures++;

        logger.error({
          [FIELDS.ERROR]: error,
          [FIELDS.OPERATION]: 'searchPlaces',
          [FIELDS.PAGE_NUMBER]: 1,
          consecutiveFailures: this.consecutiveSearchFailures
        }, 'Error searching places');

        // Check if we've hit max consecutive failures
        if (this.consecutiveSearchFailures >= this.maxConsecutiveSearchFailures) {
          logger.error({
            [FIELDS.OPERATION]: 'searchPlaces',
            [FIELDS.PAGE_NUMBER]: 1,
            [FIELDS.REASON]: 'max_search_failures_exceeded',
            consecutiveFailures: this.consecutiveSearchFailures
          }, `Places API failed ${this.consecutiveSearchFailures} consecutive times - triggering fallthrough`);

          // Navigate to fallthrough
          this.widget.setSessionData('error_context', {
            errorType: 'places_api_failed',
            operation: 'searchPlaces',
            attempts: this.consecutiveSearchFailures,
            lastError: error?.message || 'Unknown search error'
          });
          this.widget.showPage('fallthrough');
          return [];
        }

        // Show error message in dropdown after first failure
        return [{
          place_id: '__error__',
          formatted_address: 'Uh oh - not finding any places...',
          isError: true
        }];
      }
    }

    async loadGoogleMapsAPI() {
      if (window.google && window.google.maps) {
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${this.config.googleMapsApiKey}&libraries=places&loading=async&callback=initGoogleMaps`;
        script.async = true;
        script.defer = true;

        window.initGoogleMaps = () => {
          resolve();
        };

        script.onerror = () => {
          reject(new Error('Failed to load Google Maps API'));
        };

        document.head.appendChild(script);
      });
    }

    async searchPlacesWithGoogleAPI(query) {
      const { AutocompleteSuggestion } = await google.maps.importLibrary("places");

      const request = {
        input: query,
        includedRegionCodes: ['us', 'ca'], // Limit to North America
        locationRestriction: null // Global search within region
      };

      // Get autocomplete suggestions
      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      if (!suggestions || suggestions.length === 0) {
        return [];
      }

      // Convert suggestions to our expected format and get place details
      const candidates = [];
      for (const suggestion of suggestions.slice(0, 5)) {
        if (suggestion.placePrediction) {
          const place = await this.getPlaceDetailsFromId(suggestion.placePrediction.placeId);
          if (place) {
            candidates.push({
              formatted_address: place.formattedAddress || suggestion.placePrediction.text.text,
              name: suggestion.placePrediction.structuredFormat?.mainText?.text || place.displayName || suggestion.placePrediction.text.text,
              geometry: {
                location: {
                  lat: place.location?.lat() || 0,
                  lng: place.location?.lng() || 0
                }
              },
              place_id: suggestion.placePrediction.placeId
            });
          }
        }
      }

      return candidates;
    }

    async getPlaceDetailsFromId(placeId) {
      const { Place } = await google.maps.importLibrary("places");

      const place = new Place({
        id: placeId,
        requestedLanguage: 'en'
      });

      // Fetch place details
      await place.fetchFields({
        fields: ['displayName', 'formattedAddress', 'location', 'id']
      });

      return place;
    }

    renderSuggestions(suggestions) {
      const container = this.shadowRoot.querySelector('#suggestions-dropdown');

      if (!suggestions || suggestions.length === 0) {
        container.style.display = 'none';
        return;
      }

      const suggestionItems = suggestions.map(place => {
        const isError = place.isError === true;
        const cssClass = isError ? 'suggestion-item error-suggestion' : 'suggestion-item';
        return `
          <div class="${cssClass}" data-place-id="${place.place_id}" data-address="${place.formatted_address || place.name}" data-is-error="${isError}">
            ${place.formatted_address || place.name}
          </div>
        `;
      }).join('');

      container.innerHTML = suggestionItems;
      container.style.display = 'block';

      // Add click handlers for suggestions (but not for error messages)
      container.querySelectorAll('.suggestion-item').forEach(item => {
        const isError = item.dataset.isError === 'true';
        if (!isError) {
          item.addEventListener('click', () => {
            const placeId = item.dataset.placeId;
            const address = item.dataset.address;

            this.selectPlace(placeId, address);
            container.style.display = 'none';
          });
        }
      });
    }

    async selectPlace(placeId, description) {
      // Update input field immediately
      const addressInput = this.shadowRoot.querySelector('#address-input');
      addressInput.value = description;

      // Update step indicator - Step 1 completed, Step 2 active
      this.updateStepIndicator(1, 'completed');
      this.updateStepIndicator(2, 'active');

      // Show helper text (map placeholder already hidden during init)
      const helperText = this.shadowRoot.querySelector('#helper-text');
      if (helperText) {
        helperText.style.display = 'block';
        helperText.classList.add('visible');
      }

      // Retry fetching place details with exponential backoff
      await this.retryWithBackoff(
        async () => {
          // Get place details and update map
          const placeDetails = await this.getPlaceDetailsFromId(placeId);

          if (!placeDetails || !placeDetails.location) {
            throw new Error('No location data found for selected place');
          }

          const lat = placeDetails.location.lat();
          const lng = placeDetails.location.lng();

          this.updateMapLocation(lat, lng);
          this.selectedLocation = { lat, lng, description };

          // Store selected address in widget session data for use in fallthrough page
          this.widget.setSessionData('selected_address', {
            address: description,
            placeId: placeId,
            coordinates: { lat, lng },
            timestamp: new Date().toISOString()
          });
        },
        {
          maxRetries: 2,
          initialDelay: 500,
          maxDelay: 2000,
          operationName: 'selectPlace',
          errorType: 'place_selection_failed'
        }
      );
    }

    updateMapLocation(lat, lng) {
      if (!this.map) {
        logger.error({
          [FIELDS.OPERATION]: 'updateMapLocation',
          [FIELDS.PAGE_NUMBER]: 1,
          [FIELDS.REASON]: 'map_not_initialized'
        }, 'Map not initialized');
        return;
      }

      const newPosition = { lat: lat, lng: lng };
      
      // Check if this is a location change from the default
      const defaultLocation = this.config.defaultLocation || { lat: 51.0447, lng: -114.0719 };
      const isChangingFromDefault = (lat !== defaultLocation.lat || lng !== defaultLocation.lng);
      
      // Update map center and zoom, maintaining overhead view
      this.map.setCenter(newPosition);
      this.map.setZoom(18);
      this.map.setTilt(0);
      
      // Enable dragging and center pin after first location change from default
      if (isChangingFromDefault && !this.hasChangedFromDefault) {
        this.hasChangedFromDefault = true;
        this.map.setOptions({
          draggable: true,
          gestureHandling: 'cooperative'
        });

        // Add center pin functionality
        this.addCenterPin();

        // Add map movement listener to update button state
        this.addMapMoveListener();

        // Check button state immediately after address selection
        this.updateScanButtonState();
      }
    }

    addCenterPin() {
      if (!this.map) return;

      // Show the map pin elements
      const centerPinOverlay = this.shadowRoot.querySelector('#center-pin-overlay');
      const locationPulse = this.shadowRoot.querySelector('#location-pulse');
      const locationDot = this.shadowRoot.querySelector('#location-dot');

      if (centerPinOverlay) {
        centerPinOverlay.classList.add('visible');
      }
      if (locationPulse) {
        locationPulse.classList.add('visible');
      }
      if (locationDot) {
        locationDot.classList.add('visible');
      }

      this.centerPin = true; // Set flag to indicate center pin is active
    }

    isMapAtValidLocation() {
      if (!this.map) return false;

      const center = this.map.getCenter();
      const defaultLocation = this.config.defaultLocation || {
        lat: 51.0447,
        lng: -114.0719
      };

      // Compare with small tolerance for floating point precision
      const tolerance = 0.0001;
      const atDefaultLat = Math.abs(center.lat() - defaultLocation.lat) < tolerance;
      const atDefaultLng = Math.abs(center.lng() - defaultLocation.lng) < tolerance;

      return !(atDefaultLat && atDefaultLng);
    }

    updateScanButtonState() {
      if (!this.centerPin) return; // Pin must be visible

      const scanButton = this.shadowRoot.querySelector('#scan-roof-button');
      if (!scanButton) return;

      if (this.isMapAtValidLocation()) {
        scanButton.disabled = false;
        this.updateStepIndicator(2, 'completed');
        this.updateStepIndicator(3, 'active');
      } else {
        scanButton.disabled = true;
      }
    }

    addMapMoveListener() {
      if (!this.map) return;

      this.map.addListener('center_changed', () => {
        this.updateScanButtonState();
      });
    }

    async startRoofScan() {
      if (!this.centerPin || !this.map) {
        logger.error({
          [FIELDS.OPERATION]: 'startRoofScan',
          [FIELDS.PAGE_NUMBER]: 1,
          [FIELDS.REASON]: 'missing_center_pin_or_map'
        }, 'Center pin or map not available for roof scan');
        return;
      }

      // Mark step 3 as completed when user initiates scan
      this.updateStepIndicator(3, 'completed');

      // Get the current map center (where the crosshairs pin is positioned)
      const center = this.map.getCenter();
      const position = {
        lat: center.lat(),
        lng: center.lng()
      };

      // Store pin location in session for cross-page access
      this.widget.setSessionData('pin_location', {
        lat: position.lat,
        lng: position.lng,
        timestamp: new Date().toISOString()
      });

      // Switch to page 2 - it can access coordinates from session
      this.widget.showPage(2);
    }

    updateStepIndicator(stepNumber, status) {
      const step = this.shadowRoot.querySelector(`#step-${stepNumber}`);
      const divider = this.shadowRoot.querySelector(`#divider-${stepNumber}`);

      if (step) {
        // Remove all status classes
        step.classList.remove('active', 'completed');

        // Add the new status
        if (status === 'active') {
          step.classList.add('active');
        } else if (status === 'completed') {
          step.classList.add('completed');
          // Also update the divider after this step
          if (divider) {
            divider.classList.add('completed');
          }
        }
      }
    }


    showLoading(show) {
      const loadingIndicator = this.shadowRoot.querySelector('#loading-indicator');
      if (loadingIndicator) {
        loadingIndicator.style.display = show ? 'block' : 'none';
      }
    }

    hideSuggestions() {
      const container = this.shadowRoot.querySelector('#suggestions-dropdown');
      if (container) {
        container.style.display = 'none';
      }
    }

    clearSelection() {
      this.selectedLocation = null;
    }

    showError(message) {
      const errorDiv = this.shadowRoot.querySelector('#error-message');
      if (errorDiv) {
        errorDiv.textContent = message;
        errorDiv.style.display = 'block';
      }
    }

    hideError() {
      const errorDiv = this.shadowRoot.querySelector('#error-message');
      if (errorDiv) {
        errorDiv.style.display = 'none';
      }
    }
  }